import type { Condition } from 'features/charts-v2/types/yogiql'
import { FilterPreset } from 'types/project'

export enum FilterTypes {
  SELECT = 'select',
  WINDOW = 'window',
  DATE = 'date',
}

export type FilterOptionValue = {
  value: string
  // Optional: stripped to `undefined` when shown as a names-only fallback
  // while a per-panel `/filter/options/v2` fetch is in flight (PROD-2077).
  n_posts?: number
  // `cluster_uuid` is a real cluster uuid for theme filter values; on the
  // projects-group branch the backend also (mis)writes the project uuid here
  // for legacy compatibility. New code should read `proj_uuid` for project
  // filters.
  cluster_uuid?: string
  // Project uuid for proj_uuid filter values. Replaces the legacy
  // `cluster_uuid` overload on the projects-group branch.
  proj_uuid?: string
  // For proj_uuid filter values: integer project DB id used to build typed
  // SOURCE_PROJECT_ID (Int32) constraints in Charts-2 v2 summary requests.
  project_id?: number
  identifiers: string[]
}

interface FilterOptionBasis {
  alias: string
  field: string
  description?: string
  disabled?: boolean
  model?: string // Model type for custom fields (e.g., 'ProductModel', 'SourceModel')
}

export interface FilterOptionSelect extends FilterOptionBasis {
  filter_type: FilterTypes.SELECT
  values: Array<FilterOptionValue>
  customer_brands?: string[] // List of "my brands" for brand filter grouping
  customer_products?: string[] // List of "my products" for product hierarchy grouping
}

export interface FilterOptionDate extends FilterOptionBasis {
  // `Null` / `cur_min` / `cur_max` are panel-narrowed counts; stripped to
  // `undefined` in the names-only fallback (PROD-2077).
  Null?: number
  filter_type: FilterTypes.DATE
  min: number
  max: number
  value_type: number
  cur_max?: number
  cur_min?: number
}

export interface FilterOptionRange extends FilterOptionBasis {
  // `Null` / `cur_min` / `cur_max` are panel-narrowed counts; stripped to
  // `undefined` in the names-only fallback (PROD-2077).
  Null?: number
  cur_max?: number
  cur_min?: number
  filter_type: FilterTypes.WINDOW
  min: number
  max: number
  value_type: number
  step: number
}

export type FilterOption =
  | FilterOptionSelect
  | FilterOptionDate
  | FilterOptionRange

export type AppliedFilterOptionSelect = {
  field: string
  values: Array<string>
}

export type AppliedFilterOptionRange = {
  field: string
  max: number
  min: number
  value_type: number
}

export type LeftPanelFilters = {
  filterValues: AppliedFilterOption[]
  searchQuery: string[]
  searchCondition: string[]
}

export type AppliedFilterOption =
  | AppliedFilterOptionSelect
  | AppliedFilterOptionRange

export type ProjectOptionsResponse = {
  filter_options: Array<FilterOption>
}
export type FilterList = {
  isLoading: boolean
  isPageDataLoading: boolean
  setPageDataLoading: (b: boolean) => void
  changeThemeOrGroupNameInFilter: (
    entityType: string,
    entityId: string,
    newName: string,
  ) => void
  updateSelectFilterGroupValue: (
    filterField: string,
    oldName: string,
    newName: string,
  ) => void
  options: Array<FilterOption>
  openedOptions: Array<string>
  setOpenedOptions: (s: Array<string>) => void
  refetch: () => void
}
export type FilterValues = {
  values: Array<AppliedFilterOption>
  setValues: (
    list: Array<AppliedFilterOption>,
    comparativeIndex?: number,
  ) => void
  clearFilter: (fieldName: string) => void
  searchQuery: Array<AppliedFilterOption>
  setSearchQueryAndCondition: (
    query: Array<string>,
    condition: Array<string>,
    comparativeIndex?: number,
    isComparativeModeChanged?: boolean,
    isAlignFilters?: boolean,
  ) => void
  searchCondition: Array<AppliedFilterOption>

  pageCriteria: AppliedFilterOption[]
  getPageCriteriaFilter: (
    field: string,
    comparativeIndex?: number,
  ) => AppliedFilterOption | undefined
  getPageCriteriaFilterAndRemoveIdxFromName: (
    field: string,
    comparativeIndex?: number,
  ) => AppliedFilterOption | undefined
  getPageCriteriaValue: (
    field: string,
    comparativeIndex?: number,
  ) => string | undefined
  getPageCriteriaValues: (
    field: string,
    comparativeIndex?: number,
  ) => string[] | []
  appendOrReplacePageCriteriaValues: (
    filter: AppliedFilterOption,
    comparativeIndex?: number,
  ) => void
  unsetPageCriteriaValues: (field: string, comparativeIndex?: number) => void
  clearPageCriteria: () => void
  setPageCriteriaAsync: (newPageCriteria: AppliedFilterOption[]) => void
}
export type FiltersContext = {
  filterList: FilterList
  filterValues: FilterValues
  addComparativeIndexToFilterName: (
    fName: string,
    comparativeIndex: number | undefined,
  ) => string
  comparativeIndex?: number
  filterOptions?: FilterOption[]
  selectedCountry?: string
}

export type FiltersPresetContext = {
  filterPresets: FilterPreset[]
  setFilterPresets: (list: FilterPreset[]) => void
  relativeDateFilterIdx: Record<number, number>
  setRelativeDateFilterIdx: (
    idxMap:
      | Record<number, number>
      | ((prev: Record<number, number>) => Record<number, number>),
  ) => void
}
export type FilterPayload = {
  criteria: Array<AppliedFilterOption>
  search_criteria: Array<string> // deprecated
  search_terms: Array<string> // deprecated
  limit?: number
  stop_words?: Array<string>
  search_by_keywords?: boolean
  search_terms_logical?: {
    include_all?: string[] | null
    include_any?: string[] | null
    exclude_all?: string[] | null
  } | null
}
export type ApplyFilterPayload = {
  field: FilterOption['field']
  value: string | Pick<FilterOptionDate, 'min' | 'max' | 'value_type'>
}
export type ApplyGroupFilterPayload = {
  field: FilterOption['field']
  value: string[]
}

export type FilterPresetResponse = {
  filter_presets: FilterPreset[]
}

/** A saved filter as `/client/filter-presets/yogiql` lists it: themes and
 *  datasets by name, `query` null for a legacy one YogiQL cannot express. */
export type YogiQLFilterPreset = {
  name: string
  query: { yogiql: string; filters: Condition[] } | null
  unconvertible: string[]
}

/** A saved-filter list row: the legacy list's preset or the YogiQL list's. */
export type SavedFilterPreset = FilterPreset | YogiQLFilterPreset

export type SortableFilterOption = FilterOption & {
  id: string
  disabled?: boolean
}

type TimeFrame =
  | 'Week'
  | 'Month'
  | 'Quarter'
  | 'Half'
  | 'Year'
  | 'YTD'
  | 'Trailing'
  | 'Custom Date'

export interface DateRange {
  label: string
  value: string
  type?: TimeFrame
  daysCount?: number
}

export enum RelativeDateFilterIdx {
  Unset = -1,
  Last30Days,
  Last3Months,
  Last6Months,
  CurrentYear,
}
